import type { QueryBlockRequest } from '@/api/api.v2';
import { api, type QueryBlockResults, type QueryTemplate } from '@/api/api.v2';
import type { AllRequestsBlockTypes } from '@/components/Reports/Sidebar/Filter/RequestsBlock/types';
import type { Interval } from '@/components/Reports/Sidebar/Form/forms/RequestsBlock/types';
import { isValidJSON } from '@/components/WellLoggingOperations/Toolbar/modals/Templates/ui/Info/Info';
import type { RootModel } from '@/components/WellLoggingOperations/types';
import { createModel } from '@rematch/core';

type TemplateParams = { filterString: string; objectName: string };

export type RequestBlockState = {
    templates: Array<Omit<QueryTemplate, 'params'> & { params: TemplateParams }>;
    selectedTemplateId: number | null;
    modal: {
        templateName: string;
        isPrivate: boolean;
    };
    reportDataMap: Partial<Record<AllRequestsBlockTypes, QueryBlockResults>>;
    loading: {
        templates: boolean;
        reportData: boolean;
    };
    filterString: string;
    wellsResearchesIntervals: Record<string, Interval[]>;
    fluidTypes: Array<{ value: number; label: string }>;
};

const initialState: RequestBlockState = {
    templates: [],
    selectedTemplateId: null,
    modal: {
        templateName: '',
        isPrivate: false,
    },
    reportDataMap: {},
    loading: {
        templates: false,
        reportData: false,
    },
    filterString: '',
    wellsResearchesIntervals: {},
    fluidTypes: [],
};

export type RequestBlockDispatch = {
    setTemplates: (payload: Array<Omit<QueryTemplate, 'params'>>) => void;
    setModal: (payload: Partial<RequestBlockState['modal']>) => void;
    initModalProps: (payload: { templateName: string; isPrivate: boolean }) => void;
    setReportData: (payload: { reportType: AllRequestsBlockTypes; data: QueryBlockResults }) => void;
    setFluidTypes: (payload: Array<{ Title: string; ID: number }>) => void;
    setWellsResearchesIntervals: (payload: { key: string; wellsResearchesInterval: Interval[] }) => void;
    resetReportData: () => void;
    setLoading: (payload: Partial<RequestBlockState['loading']>) => void;
    setFilterString: (payload: string) => void;
    setTemplateName: (payload: string) => void;
    setSelectedTemplateId: (tId: number | null) => void;
    setIsPrivate: (payload: boolean) => void;
    loadTemplates: () => Promise<void>;
    loadReportData: (params: QueryBlockRequest) => Promise<void>;
    createTemplate: (template: Omit<QueryTemplate, 'id'>) => Promise<void>;
    updateTemplate: (template: QueryTemplate) => Promise<void>;
    deleteTemplate: (template: QueryTemplate) => Promise<void>;
};

export const requestBlock = createModel<RootModel>()({
    name: 'requestsBlock',
    state: { ...initialState },
    reducers: {
        setTemplates(state: RequestBlockState, payload: QueryTemplate[]) {
            const templates = payload.map(({ params, ...rest }) => {
                const convertedParams = isValidJSON(params)
                    ? (JSON.parse(params) as TemplateParams)
                    : { filterString: '', objectName: '' };
                return {
                    params: convertedParams,
                    ...rest,
                };
            });
            return { ...state, templates };
        },
        setSelectedTemplateId(state: RequestBlockState, payload: number | null) {
            return { ...state, selectedTemplateId: payload };
        },
        setModal(state: RequestBlockState, payload: Partial<RequestBlockState['modal']>) {
            return { ...state, modal: { ...state.modal, ...payload } };
        },
        initModalProps(
            state: RequestBlockState,
            { templateName, isPrivate }: { templateName: string; isPrivate: boolean },
        ) {
            return { ...state, modal: { templateName, isPrivate } };
        },
        resetReportData(state: RequestBlockState) {
            return {
                ...state,
                reportDataMap: {},
            };
        },
        setWellsResearchesIntervals(
            state: RequestBlockState,
            payload: { key: string; wellsResearchesInterval: Interval[] },
        ) {
            const { key, wellsResearchesInterval } = payload;
            return {
                ...state,
                wellsResearchesIntervals: { ...state.wellsResearchesIntervals, [key]: wellsResearchesInterval },
            };
        },
        setFluidTypes(state, payload: Array<{ Title: string; ID: number }>) {
            return {
                ...state,
                fluidTypes: payload.map(({ ID, Title }) => ({ value: ID, label: Title })),
            };
        },
        setReportData(
            state: RequestBlockState,
            payload: { reportType: AllRequestsBlockTypes; data: QueryBlockResults },
        ) {
            return {
                ...state,
                reportDataMap: { ...state.reportDataMap, [payload.reportType]: payload.data },
            };
        },
        setLoading(state: RequestBlockState, payload: Partial<RequestBlockState['loading']>) {
            return { ...state, loading: { ...state.loading, ...payload } };
        },
        setFilterString(state: RequestBlockState, payload: string) {
            return { ...state, filterString: payload };
        },
        setTemplateName(state: RequestBlockState, payload: string) {
            return { ...state, modal: { ...state.modal, templateName: payload } };
        },
        setIsPrivate(state: RequestBlockState, payload: boolean) {
            return { ...state, modal: { ...state.modal, isPrivate: payload } };
        },
    },
    effects: dispatch => ({
        async loadTemplates() {
            try {
                this.setLoading({ templates: true });
                const templates = await api.reports.requestsTemplate.get();
                this.setTemplates(templates);
            } finally {
                this.setLoading({ templates: false });
            }
        },

        async loadReportData(params: QueryBlockRequest) {
            try {
                this.setLoading({ reportData: true });
                const data = await api.reports.requestsBlock(params);
                const trimmedKeysData = data.map(obj =>
                    Object.fromEntries(Object.entries(obj).map(([key, value]) => [key.trim(), value])),
                );

                this.setReportData({ reportType: params.queryName, data: trimmedKeysData });
            } finally {
                this.setLoading({ reportData: false });
            }
        },

        async createTemplate(template: Omit<QueryTemplate, 'id'>) {
            try {
                this.setLoading({ templates: true });
                await api.reports.requestsTemplate.create(template);
                await dispatch.requestsBlock.loadTemplates();
            } finally {
                this.setLoading({ templates: false });
            }
        },

        async updateTemplate(template: QueryTemplate) {
            try {
                this.setLoading({ templates: true });
                await api.reports.requestsTemplate.update(template);
                await dispatch.requestsBlock.loadTemplates();
            } finally {
                this.setLoading({ templates: false });
            }
        },

        async deleteTemplate(template: QueryTemplate) {
            try {
                this.setLoading({ templates: true });
                await api.reports.requestsTemplate.delete(template);
                await dispatch.requestsBlock.loadTemplates();
            } finally {
                this.setLoading({ templates: false });
            }
        },
    }),
});
